import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { api } from "@/api/client";
import { useToast } from "@/app/toast";

import { MkEmptyState } from "./MkEmptyState";
import { MkSkeleton } from "./MkSkeleton";
import type { FabButton } from "./types";

type Box = "pending" | "notifications" | "messages" | "activities";

interface LatestAlert {
    id: number;
    title: string;
    severity: "critical" | "warning" | "info";
    model: string;
    res_id: number;
}

interface Counts {
    pending: number;
    notifications: number;
    messages: number;
    activities: number;
    latest_alert?: LatestAlert | false;
}

const SEEN_ALERT_KEY = "pm.inbox.seenAlert";

const ALERT_LOOK: Record<string, { icon: string; tone: string }> = {
    critical: { icon: "fa-exclamation-triangle", tone: "danger" },
    warning: { icon: "fa-bell", tone: "warning" },
    info: { icon: "fa-info-circle", tone: "info" },
};

function seenAlert(): number {
    try {
        return Number(window.localStorage.getItem(SEEN_ALERT_KEY)) || 0;
    } catch {
        return 0;
    }
}

function markAlertSeen(id: number) {
    try {
        window.localStorage.setItem(SEEN_ALERT_KEY, String(id));
    } catch {
        return;
    }
}

interface InboxItem {
    id: number | string;
    title: string;
    subtitle?: string;
    preview?: string;
    badge?: number;
    model?: string;
    res_id?: number;
    icon?: string;
    tone?: string;
    late?: boolean;
}

interface RawNotification {
    id: number | string; subject: string; record_name: string; author: string; preview: string;
    model: string; res_id: number; kind?: "alert"; severity?: string;
}

interface RawMessage {
    id: number; channel: string; channel_id: number; unread: number;
}

interface RawActivity {
    id: number; summary: string; activity_type: string; res_name: string;
    model: string; res_id: number;
}

interface RawPending {
    key: string; model: string; res_id: number; title: string; project: string;
    group: string; action: string; icon: string; tone: string; late: boolean; date: string;
}

const BOXES: Box[] = ["pending", "notifications", "messages", "activities"];

export function MkFabMenu({ extra = [], openRecord }: {
    extra?: FabButton[];
    /** where a record of this model lives in the application, if anywhere */
    openRecord?(model: string, id: number): void;
}) {
    const { t } = useTranslation();
    const queries = useQueryClient();
    const [panel, setPanel] = useState<string | null>(null);

    const toast = useToast();
    const counts = useQuery<{ data: Counts }>({
        queryKey: ["inbox", "counts"],
        queryFn: () => api.raw<Counts>("/inbox"),
        staleTime: 30_000,
        refetchInterval: 60_000,
        refetchIntervalInBackground: true,
        refetchOnWindowFocus: true,
    });

    const latest = counts.data?.data.latest_alert;
    useEffect(() => {
        if (!latest || latest.id <= seenAlert()) {
            return;
        }
        markAlertSeen(latest.id);
        const show = latest.severity === "critical" ? toast.danger
            : latest.severity === "warning" ? toast.warning : toast.info;
        show(latest.title, {
            title: t("mk.inbox.newAlert"),
            icon: ALERT_LOOK[latest.severity]?.icon,
            sticky: latest.severity === "critical",
            actions: [{
                label: t("mk.inbox.openAlerts"),
                onClick: () => setPanel("notifications"),
            }],
        });
    }, [latest ? latest.id : 0]);

    const box = BOXES.includes(panel as Box) ? (panel as Box) : null;

    const list = useQuery<InboxItem[]>({
        queryKey: ["inbox", box],
        enabled: box !== null,
        staleTime: 0,
        queryFn: async () => {
            if (box === "pending") {
                const payload = await api.raw<RawPending[]>("/inbox/pending");
                return payload.data.map((row, index) => ({
                    id: index,
                    title: row.title || row.group,
                    subtitle: [row.action, row.project].filter(Boolean).join(" · "),
                    preview: row.group,
                    model: row.model,
                    res_id: row.res_id,
                    icon: row.icon,
                    tone: row.tone,
                    late: row.late,
                }));
            }
            if (box === "notifications") {
                const payload = await api.raw<RawNotification[]>("/inbox/notifications");
                return payload.data.map((row) => ({
                    id: row.id,
                    title: row.subject || row.record_name || t("mk.inbox.notification"),
                    subtitle: row.author || t("mk.inbox.system"),
                    preview: row.preview,
                    model: row.model,
                    res_id: row.res_id,
                    icon: row.kind === "alert" ? ALERT_LOOK[row.severity || "info"]?.icon : undefined,
                    tone: row.kind === "alert" ? ALERT_LOOK[row.severity || "info"]?.tone : undefined,
                    late: row.kind === "alert" && row.severity === "critical",
                }));
            }
            if (box === "messages") {
                const payload = await api.raw<RawMessage[]>("/inbox/messages");
                return payload.data.map((row) => ({
                    id: row.id,
                    title: row.channel || t("mk.inbox.channel"),
                    subtitle: `${row.unread || 0} ${t("mk.inbox.unread")}`,
                    badge: row.unread || 0,
                    model: "discuss.channel",
                    res_id: row.channel_id,
                }));
            }
            const payload = await api.raw<RawActivity[]>("/inbox/activities");
            return payload.data.map((row) => ({
                id: row.id,
                title: row.summary || row.activity_type || t("mk.inbox.activity"),
                subtitle: row.res_name || "",
                model: row.model,
                res_id: row.res_id,
            }));
        },
    });

    useEffect(() => {
        if (!box || !list.data) {
            return;
        }
        const total = box === "messages"
            ? list.data.reduce((sum, item) => sum + (item.badge || 0), 0)
            : list.data.length;
        queries.setQueryData<{ data: Counts }>(["inbox", "counts"], (current) => (
            current ? { data: { ...current.data, [box]: total } } : current));
    }, [box, list.data, queries]);

    useEffect(() => {
        if (!panel) {
            return;
        }
        function onKey(event: KeyboardEvent) {
            if (event.key === "Escape") {
                setPanel(null);
            }
        }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [panel]);

    const data = counts.data?.data;
    const buttons: FabButton[] = [
        { id: "pending", icon: "fa-hourglass-half", label: t("mk.inbox.pending"),
          variant: "warning", badge: data?.pending ?? 0 },
        { id: "notifications", icon: "fa-bell", label: t("mk.inbox.notifications"),
          variant: "primary", badge: data?.notifications ?? 0 },
        { id: "messages", icon: "fa-comments", label: t("mk.inbox.messages"),
          variant: "info", badge: data?.messages ?? 0 },
        { id: "activities", icon: "fa-clock-o", label: t("mk.inbox.activities"),
          variant: "warning", badge: data?.activities ?? 0 },
        ...extra,
    ];

    const current = buttons.find((button) => button.id === panel);

    function openPanel(button: FabButton) {
        if (button.onClick) {
            button.onClick();
            setPanel(null);
            return;
        }
        setPanel((open) => (open === button.id ? null : button.id));
    }

    function openItem(item: InboxItem) {
        if (!item.model || !item.res_id || !openRecord) {
            return;
        }
        openRecord(item.model, item.res_id);
        setPanel(null);
    }

    const items = list.data ?? [];

    return (
        <div className="mk-fabmenu">
            {panel && <div className="mk-fabmenu-scrim" onClick={() => setPanel(null)} />}

            {panel && box && (
                <div className="mk-fabmenu-panel">
                    <div className="mk-fabmenu-panel-head">
                        <span className="mk-fabmenu-panel-title">{current?.label}</span>
                        <button className="mk-fabmenu-panel-close" onClick={() => setPanel(null)}
                                aria-label={t("mk.close")}>
                            <i className="fa fa-times" />
                        </button>
                    </div>
                    <div className="mk-fabmenu-panel-body">
                        {list.isLoading ? (
                            <div className="mk-inbox-loading">
                                <MkSkeleton lines={6} />
                            </div>
                        ) : !items.length ? (
                            <MkEmptyState icon="fa-check-circle" title={t("mk.inbox.caughtUp")} />
                        ) : items.map((item) => (
                            <button key={item.id}
                                    className={`mk-inbox-item${item.late ? " mk-inbox-item-late" : ""}`}
                                    onClick={() => openItem(item)}>
                                {item.icon && (
                                    <span className={`mk-inbox-item-icon mk-inbox-item-${item.tone || "info"}`}>
                                        <i className={`fa ${item.icon}`} />
                                    </span>
                                )}
                                <div className="mk-inbox-item-main">
                                    <div className="mk-inbox-item-title">{item.title}</div>
                                    {item.subtitle && (
                                        <div className="mk-inbox-item-sub">{item.subtitle}</div>
                                    )}
                                    {item.preview && (
                                        <div className="mk-inbox-item-preview">{item.preview}</div>
                                    )}
                                </div>
                                {item.badge ? (
                                    <span className="mk-inbox-item-badge">{item.badge}</span>
                                ) : null}
                                <i className="fa fa-angle-right mk-inbox-item-go" />
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <div className="mk-fabmenu-bar">
                {buttons.map((button) => (
                    <button key={button.id}
                            className={`mk-fabmenu-btn${panel === button.id ? " mk-fabmenu-btn-active" : ""}`}
                            title={button.label} aria-label={button.label}
                            aria-expanded={panel === button.id}
                            onClick={() => openPanel(button)}>
                        <i className={`fa ${button.icon}`} />
                        {button.badge && button.badge > 0 ? (
                            <span className={`mk-fabmenu-badge mk-fabmenu-badge-${button.variant || "primary"}`}>
                                {button.badge > 99 ? "99+" : button.badge}
                            </span>
                        ) : null}
                    </button>
                ))}
            </div>
        </div>
    );
}
